import strings from "global/constants/StringConstants";
import {
  IDV_SECURITY_OPTIONS,
  IdvSecurityKey,
} from "helpers/identityVerificationHelpers";
import { IdvMethod } from "types/identityVerification";
import { toPhoneParts } from "helpers/methods";

/**
 * Types and validation for the rebuilt bulk send flow.
 *
 * Shapes follow the backend spec ("Bulk Send Rebuilt", branch
 * `bulk-send-flow-enhancement`). A batch is template-anchored: the documents,
 * the field placement, the signer slots and the signing order all come from a
 * template, and each row of the recipient list becomes one envelope.
 *
 * Nothing here is verified against a live server yet — the endpoints are not
 * deployed. Keep every field optional that the spec does not guarantee.
 */

/**
 * Wizard steps, in order — named after the create-request flow's own steps so
 * a sender reads the same words in both places. "Signers" is what that flow
 * already calls its recipients step in bulk mode, rather than "Approvers and
 * Signers", since a batch has no approvers.
 *
 * "Recipients" and "Review & Send" have no counterpart there: the first is the
 * list a batch fans out to, the second replaces "Email configurations"
 * because it both carries the subject and message *and* sends.
 */
export const BULK_STEPS = [
  "Upload Document",
  "Sign Request Editor",
  "Signers",
  "Recipients",
  "Review & Send",
] as const;

export type BulkStep = (typeof BULK_STEPS)[number];

/** Which door the sender came in by. A template arrives with its fields already placed. */
export type BulkSendDoor = "template" | "upload";

/**
 * How a role proves who they are before signing. The identity verification
 * kinds are `IDV_SECURITY_OPTIONS`' keys, shared with the single send.
 */
export type BulkSecurityKind = "none" | "otp" | "code" | "sms" | IdvSecurityKey;

/** Word for word the create-request flow's own security labels. */
export const BULK_SECURITY_LABELS: Record<BulkSecurityKind, string> = {
  none: "No security",
  otp: "Email OTP",
  code: "Access Code",
  sms: strings.smsDelivery.securityPhoneLabel,
  ...(Object.fromEntries(
    IDV_SECURITY_OPTIONS.map((option) => [option.key, option.label]),
  ) as Record<IdvSecurityKey, string>),
};

/** Channels the signing link can reach a role by. */
export type BulkDeliveryChannel = "EMAIL" | "SMS" | "WHATSAPP";

export const BULK_CHANNEL_LABELS: Record<BulkDeliveryChannel, string> = {
  EMAIL: "Email",
  SMS: "Text",
  WHATSAPP: "WhatsApp",
};

/**
 * The channels a sender can actually pick. WhatsApp delivery is disabled in
 * the create-request flow too — uncomment there and here together. The label
 * above stays complete so a template already set to WhatsApp still reads
 * correctly in the summaries.
 */
export const BULK_CHANNEL_OPTIONS: BulkDeliveryChannel[] = ["EMAIL", "SMS"];

/** A role reached by text needs a phone, wherever that phone comes from. */
export const needsPhoneFor = (channels: BulkDeliveryChannel[]): boolean =>
  channels.some((channel) => channel !== "EMAIL");

/**
 * One signer slot on the template. `signerIndex` is the slot's id, and it is
 * also what every field placed for that slot is bound to.
 */
export interface BulkSignerSlot {
  signerIndex: number;
  /** Role name. Falls back to `Signer 1`, `Signer 2` when the template left it blank. */
  label: string;
  order: number;
  cc?: boolean;
  /** The slot is texted or phone-verified, so a fixed signer for it needs a phone. */
  needsPhone?: boolean;
  /** How the link reaches this role. Empty is treated as email-only. */
  channels?: BulkDeliveryChannel[];
  /**
   * How this role proves who they are. The slots endpoint sends flags; they are
   * normalised to this flow's own word by `normaliseSlots`.
   */
  security?: BulkSecurityKind;
  /** The code set for this slot, when a template already carries one. */
  accessCode?: string;
  /** The template's own number for this slot, as a display string. */
  phone?: string;
  /** How long after the level before it this role is asked to sign. */
  delayDays?: number;
  delayHours?: number;
  /** Field labels placed for this slot, for the "Signature, Full name, Date" note. */
  fieldLabels?: string[];
}

/**
 * The security flags a slot carries. Deliberately flags only: a template's
 * access code, OTP and auth phone are the template author's secrets, and the
 * server does not hand them to whoever is building a batch.
 */
export interface BulkSlotSecurityFlags {
  needSecuredAccess?: boolean;
  needOTPValidation?: boolean;
  needPhoneValidation?: boolean;
  identityVerificationMethods?: IdvMethod[];
}

/** Response of the document → hidden template call, which drives the Signers step. */
export interface BulkTemplateFromDocument {
  templateId: string;
  name?: string;
  signerSlots: BulkSignerSlot[];
}

/** A phone as the backend wants it — an object, never a string. */
export interface BulkPhone {
  countryCode: string;
  number: string;
}

/** A slot pinned for the whole batch: the same person on every copy. */
export interface BulkFixedSigner {
  signerIndex: number;
  name: string;
  email: string;
  phone?: BulkPhone;
  /** Only when the role's security is an access code — one code, one person. */
  accessCode?: string;
}

/** One slot filled from the recipient list, for a single row. */
export interface BulkRowRecipient {
  signerIndex: number;
  name: string;
  email: string;
  phone?: BulkPhone;
  /**
   * The access code this recipient will be asked for. Per row, never per
   * batch: 500 envelopes sharing one code is 500 people able to open each
   * other's copy.
   */
  accessCode?: string;
}

export interface BulkRow {
  /** The real file line for a CSV row, so an error points at the line to edit. */
  rowNumber?: number;
  recipients: BulkRowRecipient[];
  /** Prefill, keyed by slot id then by the field's label. */
  fieldValues?: Record<string, Record<string, string>>;
}

/** How often a recipient is nudged, and when the batch stops being signable. */
export interface BulkReminderSettings {
  needReminders: boolean;
  daysBeforeFirstReminder: number;
  daysBetweenReminders: number;
  expirationDays: number;
}

/** What a recipient may do with their copy. Mirrors `signRequestConfig`. */
export interface BulkPermissionSettings {
  attachDocumentToMailOnCompletion: boolean;
  allowDocumentDownload: boolean;
  sendNotificationOnCompletion: boolean;
  allowSignatureDelegation: boolean;
}

/** Body of the create call. Creating validates and saves a draft; it sends nothing. */
export interface BulkBatchDefinition {
  name: string;
  templateId: string;
  workspaceId?: string;
  /** May carry merge tokens; the server substitutes them per row. */
  emailSubject: string;
  emailMessage: string;
  expirationDays?: number;
  needReminders?: boolean;
  reminders?: {
    daysBeforeFirstReminder: number;
    daysBetweenReminders: number;
  } | null;
  /** Absent or empty means send as soon as the batch is queued. */
  scheduleTime?: string;
  signRequestConfig?: BulkPermissionSettings;
  fixedSigners?: BulkFixedSigner[];
  rows?: BulkRow[];
}

/** Everything the Review step can set, kept together so it travels as one. */
export interface BulkBatchSettings {
  batchName: string;
  emailSubject: string;
  emailMessage: string;
  reminders: BulkReminderSettings;
  permissions: BulkPermissionSettings;
  /** Empty string means "send immediately". */
  scheduleTime: string;
}

export const BULK_SUBJECT_MAX = 100;
export const BULK_MESSAGE_MAX = 1000;

/** What the app defaults a batch to before the sender touches anything. */
export const defaultBatchSettings = (): Omit<
  BulkBatchSettings,
  "batchName" | "emailSubject" | "emailMessage"
> => ({
  reminders: {
    needReminders: true,
    daysBeforeFirstReminder: 5,
    daysBetweenReminders: 3,
    expirationDays: 180,
  },
  permissions: {
    attachDocumentToMailOnCompletion: true,
    allowDocumentDownload: true,
    sendNotificationOnCompletion: true,
    allowSignatureDelegation: true,
  },
  scheduleTime: "",
});

/**
 * Columns a merge token can name. Recipient name and email always exist; the
 * rest are the template's own prefillable field labels.
 */
export interface BulkMergeToken {
  token: string;
  label: string;
}

export type BulkIssueSeverity = "error" | "warning";

/**
 * One problem with one row. `error` rows are stored and never sent; `warning`
 * rows (a repeated recipient set) send normally.
 */
export interface BulkValidationIssue {
  rowNumber: number;
  /** The CSV header, e.g. `Student::Email`. */
  column: string;
  /**
   * The cell the header refers to. Sent alongside `column` because matching a
   * header string back to a role breaks as soon as two roles share a label.
   */
  signerIndex?: number;
  field?: BulkCellField;
  severity: BulkIssueSeverity;
  message: string;
}

/**
 * One row exactly as the server parsed it out of the file — the values it
 * read, not cleaned-up ones, and whether it made the batch.
 *
 * This is what turns an upload from a dead end into something a sender can
 * work with: the rows come back, fill the same grid the typed route uses, and
 * the bad cells are fixed where they are rather than in Excel.
 */
export interface BulkParsedRow {
  /** The real line in the file, so a message names the row the sender sees. */
  rowNumber: number;
  accepted?: boolean;
  recipients: BulkRowRecipient[];
}

export interface BulkCreateResponse {
  batchId: string;
  totalRows: number;
  validRows: number;
  issues?: BulkValidationIssue[];
  /**
   * Present once the server returns what it parsed. Absent on an older
   * server, in which case the upload falls back to a read-only report.
   */
  rows?: BulkParsedRow[];
  /**
   * What the send will cost in SMS credits, and what the account holds.
   * Priced off the *valid* rows, not the total — a batch with bad rows does
   * not spend credits on them. Both zero when the template texts nobody.
   */
  smsCreditsRequired?: number;
  smsCreditBalance?: number;
  /**
   * The active workspace's envelope quota, the same way: the batch needs one
   * envelope per valid row. All absent when the workspace has no quota.
   * `workspaceQuotaResetsOn` is the first day of the next period (YYYY-MM-DD).
   */
  workspaceQuota?: number;
  workspaceEnvelopesLeft?: number;
  workspaceQuotaResetsOn?: string;
}

export interface BulkSendResponse {
  batchId: string;
  queuedRows: number;
}

/** Batch lifecycle. `CompletedWithErrors` is its own state: it means rows are worth retrying. */
export type BulkBatchStatus =
  | "Draft"
  | "Queued"
  | "Running"
  | "Completed"
  | "CompletedWithErrors"
  | "Cancelled";

/** Per-row status. These are the keys `statusCounts` is keyed by. */
export type BulkRowStatus =
  "Pending" | "Sending" | "Sent" | "Failed" | "Cancelled";

export interface BulkBatch {
  id: string;
  name: string;
  templateId?: string;
  status: BulkBatchStatus;
  totalRows: number;
  fixedSigners?: BulkFixedSigner[];
  /** Derived per request server-side, so these never need reconciling. */
  statusCounts?: Partial<Record<BulkRowStatus, number>>;
  createdBy?: string;
  createdOn?: string;
}

export interface BulkBatchRow {
  rowNumber: number;
  recipients: BulkRowRecipient[];
  status: BulkRowStatus;
  /** Present once an envelope is reserved, even when the row failed. */
  envelopeId?: string;
  /** Written for the sender. Render as-is rather than mapping to our own copy. */
  errorMessage?: string;
  attempts?: number;
  sentOn?: string | null;
}

/** One envelope in a batch waiting on the caller to sign. */
export interface BulkSigningQueueEntry {
  envelopeId: string;
  emailSubject?: string;
  createdOn?: string;
}

export interface BulkRecipientList {
  id: string;
  name: string;
  rowCount?: number;
  createdOn?: string;
}

/** A batch tops out at 1000 rows. The old flow's "max 500" was display text only. */
export const BULK_MAX_ROWS = 1000;

/** How a role is filled: from the recipient list, or pinned for the whole batch. */
export type BulkRoleMode = "vary" | "fixed";

/** The Signers step's working state for one template slot. */
export interface BulkRoleAssignment {
  slot: BulkSignerSlot;
  mode: BulkRoleMode;
  /** Only meaningful while `mode` is `fixed`. */
  name: string;
  email: string;
  phone: string;
  /**
   * Delivery and security are the role's, not the person's, so they apply to
   * every copy whether the role varies or is pinned. Edited here, they travel
   * with the batch's own template rather than changing the source one.
   */
  channels: BulkDeliveryChannel[];
  security: BulkSecurityKind;
  /**
   * The access code, when `security` is `code`. It only belongs to a pinned
   * role — a role that varies gets a code per recipient, in the list.
   */
  accessCode: string;
  /**
   * Hold this role back after the level before it. Only meaningful past the
   * first level, and the reason it matters in bulk: a pinned counter-signer
   * otherwise receives every copy at once.
   */
  delayDays: number;
  delayHours: number;
}

/**
 * Does this role need a phone number at all?
 *
 * Two separate reasons, either of which is enough: the signing link is texted
 * to them, or their identity is checked by an SMS code. A role set to email
 * delivery *and* SMS OTP still needs a number — the code has to reach them
 * somehow. When both apply it is the same one number, asked for once.
 *
 * Read from the assignment, never from the template slot beneath it: the
 * sender may have just changed either setting on this very step.
 */
export const roleNeedsPhone = (assignment: BulkRoleAssignment): boolean =>
  needsPhoneFor(assignment.channels) || assignment.security === "sms";

/**
 * Which setting is actually asking for the number, named so the sender knows
 * what to change if they'd rather not supply one. Takes the live channels and
 * security rather than a slot, so a change made on the Signers step is what
 * the wording reflects.
 */
export const phoneReasonFor = (
  channels: BulkDeliveryChannel[],
  security: BulkSecurityKind,
): string => {
  const texted = needsPhoneFor(channels);
  const smsCode = security === "sms";
  if (texted && smsCode) return "Text delivery and SMS OTP";
  return smsCode ? "SMS OTP" : "Text delivery";
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

/**
 * An access code is typed back by the recipient on a phone, so it is bounded
 * at both ends: long enough not to be guessed, short enough to be read out,
 * and with no spaces to argue about when it is pasted.
 */
export const BULK_ACCESS_CODE_MIN = 4;
export const BULK_ACCESS_CODE_MAX = 20;

/** The one place the rule lives — pinned roles and list rows both read it. */
export const bulkAccessCodeError = (value?: string): string | undefined => {
  const code = (value || "").trim();
  if (!code) return "Access code is required";
  if (/\s/.test(code)) return "Access code cannot contain spaces";
  if (code.length < BULK_ACCESS_CODE_MIN)
    return `Use at least ${BULK_ACCESS_CODE_MIN} characters`;
  if (code.length > BULK_ACCESS_CODE_MAX)
    return `Use at most ${BULK_ACCESS_CODE_MAX} characters`;
  return undefined;
};

export const isBulkEmailValid = (value?: string): boolean =>
  EMAIL_PATTERN.test((value || "").trim());

/**
 * One recipient row's verdict, derived from the row itself. The table's inline
 * marks, the check panel's banner, its chips, its worklist and its footer all
 * read this — so they cannot disagree about how many rows are ready.
 */
export type BulkRowVerdict =
  | "ready"
  /** Something typed is wrong — a malformed address, a too-short number. */
  | "invalid"
  /** Something required was never filled in. */
  | "incomplete"
  | "blank"
  | "skipped";

/** One thing wrong with one cell, so every bad cell can be marked at once. */
/** Every cell the recipient table can ask for, and so can flag. */
export type BulkCellField = "name" | "email" | "phone" | "code";

export interface BulkCellIssue {
  signerIndex: number;
  field: BulkCellField;
  message: string;
  /** Missing reads as incomplete; malformed reads as invalid. */
  kind: "missing" | "malformed";
}

export interface BulkRowCheck {
  /** 0-based index into the rows array; the label shown is `Row {index + 1}`. */
  index: number;
  verdict: BulkRowVerdict;
  /** Who the row is for, as far as it has been filled in. */
  name: string;
  /** Why it is flagged, written for the sender. Empty when nothing is wrong. */
  reason: string;
  /** Everything wrong with the row — one entry per offending cell. */
  issues: BulkCellIssue[];
  /** The first issue's slot, which is what "Fix above" focuses. */
  signerIndex?: number;
  /** The first issue's cell. */
  field?: BulkCellField;
}

export interface BulkRowCheckSummary {
  checks: BulkRowCheck[];
  /** Rows that will actually send. */
  ready: number;
  invalid: number;
  incomplete: number;
  blank: number;
  skipped: number;
  /** Everything the sender typed something into — blank rows don't count. */
  checked: number;
  /** The rows the worklist lists: flagged, not blank, not skipped. */
  flagged: BulkRowCheck[];
}

export interface BulkRoleErrors {
  [signerIndex: number]: {
    name?: string;
    email?: string;
    phone?: string;
    code?: string;
  };
}

/**
 * The Signers step. At least one role has to vary, or there is nothing for the
 * recipient list to fill; every pinned role needs a name and a valid email, and
 * a phone too when its slot is texted or phone-verified.
 */
export const validateBulkRoles = (
  assignments: BulkRoleAssignment[],
): { isValid: boolean; errors: BulkRoleErrors; formError?: string } => {
  const errors: BulkRoleErrors = {};
  let isValid = true;

  const varying = assignments.filter((a) => a.mode === "vary");
  if (varying.length === 0) {
    return {
      isValid: false,
      errors,
      formError:
        "At least one role has to vary per recipient — that's the column your list fills.",
    };
  }

  assignments.forEach((assignment) => {
    if (assignment.mode !== "fixed") return;

    const rowErrors: BulkRoleErrors[number] = {};
    if (!assignment.name.trim()) {
      rowErrors.name = "Enter a name";
    }
    if (!assignment.email.trim()) {
      rowErrors.email = "Enter an email";
    } else if (!isBulkEmailValid(assignment.email)) {
      rowErrors.email = "Enter a valid email";
    }
    /*
      The same test the payload applies. The contact control seeds itself with
      a dial code, so `"+1"` is not a number — accepting it here creates a
      phone-verified signer with no number, and an envelope nobody can open.
    */
    if (roleNeedsPhone(assignment) && !toPhoneParts(assignment.phone)) {
      rowErrors.phone = `A number is required for ${phoneReasonFor(
        assignment.channels,
        assignment.security,
      )}`;
    }
    if (assignment.security === "code") {
      const codeError = bulkAccessCodeError(assignment.accessCode);
      if (codeError) rowErrors.code = codeError;
    }

    if (Object.keys(rowErrors).length > 0) {
      isValid = false;
      errors[assignment.slot.signerIndex] = rowErrors;
    }
  });

  return { isValid, errors };
};

/** The Review step. Subject and message are batch-level, as they are today. */
export const validateBulkReview = (
  batchName: string,
  emailSubject: string,
  emailMessage: string,
): string | undefined => {
  if (!batchName.trim()) return "Give this batch a name.";
  if (!emailSubject.trim()) return "Enter an email subject.";
  if (emailMessage.length > 1000)
    return "Email message must not exceed 1000 characters.";
  return undefined;
};
